<script setup lang="ts">
import { motion } from "motion-v";
import { computed, onNuxtReady, ref } from "#imports";
import type { StylesElementVersion, StylesSizes } from "#imports";
type ButtonSize = StylesSizes;
type ButtonType = "submit" | "reset" | "button";
const props = withDefaults(
  defineProps<{
    label?: string | null;
    icon?: string | null;
    iconColor?: string | null;
    ariaLabel?: string | null;
    rounded?: boolean;
    size?: ButtonSize;
    version?: StylesElementVersion;
    outline?: boolean;
    block?: boolean;
    link?: boolean;
    tight?: boolean;
    reverse?: boolean;
    backgroundColor?: string;
    inline?: boolean;
    hideLabel?: boolean;
    disabled?: boolean;
    type?: ButtonType;
    isLoading?: boolean;
    notification?: boolean;
    notificationColor?: string;
  }>(),
  {
    type: "button",
    version: "default",
    size: "medium",
    icon: null,
    iconColor: null,
    label: null,
    ariaLabel: null,
    notification: false,
    notificationColor: "var(--color-rose-500)",
    backgroundColor: undefined,
  },
);
const appLoaded = ref<boolean>(false);
onNuxtReady(() => {
  appLoaded.value = true;
});

const buttonClasses = computed(() => {
  return [
    props.size ? props.size : "medium",
    props.rounded ? "rounded" : "",
    props.outline ? "outline" : "",
    props.block ? "block" : "",
    props.link ? "link" : "",
    props.tight ? "tight" : "",
    props.reverse ? "reverse" : "",
    props.version ?? "default",
    props.icon && props.hideLabel ? "icon-only" : "",
    props.inline ? "inline" : "",
    props.isLoading ? "loading" : "",
  ].join(" ");
});
</script>

<template>
  <button
    class="mp-button typeface-summary"
    :class="buttonClasses"
    :aria-label="props.ariaLabel ?? props.label ?? undefined"
    :style="{
      backgroundColor: props.backgroundColor || undefined,
      color: props.iconColor || undefined,
    }"
    :type="props.type"
    :disabled="props.disabled || !appLoaded"
  >
    <i
      v-if="props.icon && !props.isLoading"
      :class="`${props.icon} icon`"
      :style="{ opacity: props.isLoading ? 0.5 : 1 }"
    />
    <label v-if="!props.hideLabel && props.label">
      <span>
        <i v-if="props.isLoading" class="ri-loader-4-line loading-indicator" />
        <motion.span
          v-if="props.isLoading"
          :initial="{ opacity: 0 }"
          :animate="{ opacity: 1 }"
          :transition="{ type: 'spring', duration: 0.5 }"
        >
          {{ props.label }}
        </motion.span>
        <motion.span
          v-else
          :initial="{ opacity: 0 }"
          :animate="{ opacity: 1 }"
          :transition="{ type: 'spring', duration: 0.5 }"
        >
          {{ props.label }}
        </motion.span>
      </span>
    </label>
    <div v-if="props.notification" class="notification" />
    <slot />
  </button>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";

.mp-button {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  outline: 0 none;
  border: 0 none;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 0.5rem;
  margin: 0;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease-in;
  label {
    cursor: pointer;
  }
  i {
    cursor: pointer;
    font-size: 1rem;
  }
  .icon + label {
    margin-left: 0.5rem;
  }
  &.disabled,
  &:disabled {
    opacity: 0.2 !important;
    cursor: not-allowed;
    transition: opacity 100ms ease-in;
    label {
      cursor: not-allowed;
    }
  }
  &.rounded {
    border-radius: 2em;
  }
  &.xs {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
  &.small {
    font-size: 0.875rem;
    padding: 0.45rem 0.75em;

    &.loading {
      padding-left: 2rem !important;
      transition: padding 0.3s ease-in-out;
    }

    .loading-indicator {
      position: absolute;
      top: 0.2rem;
      left: 0.5rem;
      animation: spin 2s linear infinite;
      font-size: 1rem !important;
    }
  }
  &.medium {
    padding: 0.6rem 1em;

    &.loading {
      padding-left: 2.5rem !important;
      transition: padding 0.3s ease-in-out;
    }

    .loading-indicator {
      position: absolute;
      top: 0.2rem;
      left: 0.5rem;
      animation: spin 2s linear infinite;
      font-size: 1.5rem !important;
    }
    @mixin screen xs {
      font-size: 1rem;
      padding: 0.5rem 1rem;
    }
  }
  &.large {
    padding: 0.75rem 1.75rem;
    font-size: 1rem;

    &.loading {
      padding-left: 2.5rem !important;
      transition: padding 0.3s ease-in-out;
    }

    .loading-indicator {
      position: absolute;
      top: 0.5rem;
      left: 0.5rem;
      animation: spin 2s linear infinite;
      font-size: 1.5rem !important;
    }
  }
  &.tight {
    padding: 0.25rem 0.55rem;
  }
  &.default {
    background-color: var(--color-slate-900);
    color: white;
    border: 1px solid var(--color-slate-800);
    &:hover {
      background-color: var(--color-slate-800);
    }
  }
  &.secondary {
    background-color: var(--color-slate-100);
    color: var(--color-slate-900);
    border: 1px solid var(--color-slate-200);
    &:hover {
      background-color: var(--color-slate-100);
    }
  }
  &.primary {
    background-color: var(--color-rose-500);
    color: white;
    &:hover {
      background-color: var(--color-rose-600);
    }
  }
  &.success,
  &.save {
    background-color: var(--color-green-500);
    color: white;
    &:hover {
      background-color: var(--color-green-600);
    }
  }
  &.info {
    background-color: var(--color-indigo-500);
    color: white;
    &:hover {
      background-color: var(--color-indigo-600);
    }
  }

  &.transparent {
    background: rgba(164, 164, 164, 0.5);
    color: white;
    &:hover {
      background: rgba(164, 164, 164, 0.8);
    }
  }
  &.warning {
    background-color: var(--color-orange-500);
    color: white;
    &:hover {
      background-color: var(--color-orange-600);
    }
  }
  &.danger,
  &.error {
    background-color: var(--color-rose-500);
    color: white;
    &:hover {
      background-color: var(--color-rose-600);
    }
  }
}

.link {
  &.default {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-slate-600);
    &:hover {
      color: var(--color-slate-800);
      background-color: transparent;
      font-weight: bold;
    }
  }
  &.secondary {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-slate-400);
  }
  &.primary {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-rose-400);
  }
  &.save {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-green-400);
  }
  &.info {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-indigo-400);
  }
  &.danger {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--color-rose-500);
  }
}

.reverse {
  display: flex !important;
  gap: 0.5rem;
  flex-direction: row-reverse;
}

.inline {
  padding-left: 0;
  padding-right: 0;
}

.outline {
  background-color: transparent;
  &.default {
    background-color: transparent;
    color: var(--color-slate-900);
    border: 1px solid var(--color-slate-900);
  }
  &.secondary {
    background-color: transparent;
    color: var(--color-slate-50);
    border: 1px solid var(--color-slate-50);
  }
  &.primary {
    background-color: transparent;
    color: var(--color-rose-400);
    border: 1px solid var(--color-rose-400);
  }
  &.save {
    background-color: transparent;
    color: var(--color-green-400);
    border: 1px solid var(--color-green-400);
  }
  &.info {
    background-color: transparent;
    color: var(--color-indigo-400);
    border: 1px solid var(--color-indigo-400);
  }
  &.danger {
    background-color: transparent;
    color: var(--color-rose-500);
    border: 1px solid var(--color-rose-500);
  }
}

.icon-only {
  &.small,
  &.medium,
  &.large {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 !important;

    i {
      font-size: 1.25rem;
      margin: 0 !important;
    }
  }

  &.small {
    min-width: 2rem;
    min-height: 2rem;
    i {
      font-size: 1rem;
      /* Making small icon only buttons circular */
      border-radius: 100%;
      padding-top: 0.55rem;
      padding-bottom: 0.55rem;
      padding-left: 0.6rem;
      padding-right: 0.6rem;
    }
  }

  &.medium {
    min-width: 2.5rem;
    min-height: 2.5rem;
    i {
      font-size: 1.25rem;
    }
  }

  &.large {
    min-width: 3rem;
    min-height: 3rem;
    i {
      font-size: 1.5rem;
    }
  }
}

.no-border {
  border: none !important;
}

.notification {
  position: absolute;
  background-color: v-bind("props.notificationColor");
  top: 0;
  right: 0;
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 100%;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes fade {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
